import { ProtectedRoute } from "@/components/protected-route";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Outlet } from "react-router";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { NavUser } from "./nav-user";
import { NavMain } from "./nav-main";
import { ContactUsCard } from "./contact-us-card";
import { useLocation } from "react-router";
import { Fragment } from "react/jsx-runtime";
import { AddPaymentMethodCard } from "./add-payment-method-card";
import { usePaymentMethods } from "@/api/use-payment-methods-api";
import { useSession } from "@/services/auth0/session";
import { PaymentStyle } from "@/api.types";
import { useCustomerOrders } from "@/api/use-order";

export const ProtectedLayout = () => {
  const location = useLocation();
  const pathSegments = location.pathname
    .split("/")
    .filter((segment) => segment)
    .map((segment) =>
      segment
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );

  return (
    <ProtectedRoute>
      <SidebarProvider
        style={
          {
            "--sidebar-width": "380px",
            "--sidebar-width-mobile": "100%",
          } as React.CSSProperties
        }
      >
        <AppSidebar />
        <SidebarInset className="w-full overflow-hidden flex flex-col">
          <header className="sticky top-0 z-10 flex h-16 shrink-0 items-center gap-2 border-b bg-background px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb className="overflow-x-auto">
              <BreadcrumbList>
                {pathSegments.map((segment, index) => (
                  <Fragment key={segment.toLowerCase()}>
                    <BreadcrumbItem>
                      <BreadcrumbPage>{segment}</BreadcrumbPage>
                    </BreadcrumbItem>
                    {index < pathSegments.length - 1 && <BreadcrumbSeparator />}
                  </Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </header>
          <main className="flex-1 overflow-auto p-4">
            <Outlet />
          </main>
        </SidebarInset>
      </SidebarProvider>
    </ProtectedRoute>
  );
};

export function AppSidebar() {
  const { customer } = useSession();
  const { data: paymentMethods, isLoading: isPaymentMethodsLoading } =
    usePaymentMethods();
  const { data: ordersResponse } = useCustomerOrders({ page: 1, limit: 1 });
  const hasNoPaymentMethods = paymentMethods?.paymentMethods.length === 0;
  const totalOrders = ordersResponse?.metadata.totalCount ?? 0;

  const shouldShowAddPaymentMethodCard =
    customer?.billing?.paymentStyle !== PaymentStyle.MANUAL &&
    hasNoPaymentMethods;

  return (
    <Sidebar>
      <SidebarHeader>
        <NavUser />
      </SidebarHeader>
      <SidebarContent>
        <NavMain />
      </SidebarContent>
      <SidebarFooter>
        {isPaymentMethodsLoading ? (
          <div className="h-5 w-1" />
        ) : (
          shouldShowAddPaymentMethodCard && (
            <AddPaymentMethodCard
              severity={totalOrders > 0 ? "error" : "warning"}
            />
          )
        )}
        <div className="h-5 w-1" />
        <ContactUsCard />
        <div className="w-full h-[70px] md:h-[100px] bg-transparent flex justify-center items-end pb-4">
          <span className="text-xs md:text-md text-muted-foreground">
            v{process.env.npm_package_version ?? "1.0.0"}
          </span>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
